import axios from "axios";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import API_URL from "../../config";
import { toast } from "react-toastify";

const TABS = [
  { key: "ALL", label: "All Activity" },
  { key: "PAGE", label: "Page Activity" },
  { key: "CLICK", label: "Clicks" },
  { key: "API_REQUEST", label: "API Requests" },
  { key: "API_RESPONSE", label: "API Responses" },
];

const getBadgeClass = (event) => {
  switch (event.type) {
    case "PAGE_ENTER":
      return "badge bg-primary";

    case "PAGE_EXIT":
      return "badge bg-p text-white";

    case "CLICK":
      return "badge bg-p text-white";

    case "API_REQUEST":
      return "badge bg-p text-white";

    case "API_RESPONSE":
      return event.success ? "badge bg-success" : "badge bg-danger";

    default:
      return "badge bg-p text-white";
  }
};

const getEventIcon = (event) => {
  switch (event.type) {
    case "PAGE_ENTER":
      return "→";

    case "PAGE_EXIT":
      return "←";

    case "CLICK":
      return "●";

    case "API_REQUEST":
      return "↑";

    case "API_RESPONSE":
      return event.success ? "✓" : "⚠";

    default:
      return "•";
  }
};

const getEventTitle = (event) => {
  switch (event.type) {
    case "PAGE_ENTER":
      return "Page Visited";

    case "PAGE_EXIT":
      return "Page Left";

    case "CLICK":
      return "User Interaction";

    case "API_REQUEST":
      return "Data Requested";

    case "API_RESPONSE":
      return event.success ? "Data Loaded Successfully" : "Request Failed";

    default:
      return "User Activity";
  }
};

const formatTime = (timestamp) => {
  if (!timestamp) return "Unknown time";

  return new Date(timestamp).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const formatShortTime = (timestamp) => {
  if (!timestamp) return "";

  return new Date(timestamp).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getRelativeDate = (timestamp) => {
  if (!timestamp) return "Unknown date";

  const date = new Date(timestamp);

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function ActivityDetails({ event }) {
  const [showTechnical, setShowTechnical] = useState(false);

  return (
    <div className="mt-2">
      <div className="activity-description">{event.description}</div>

      {event.type === "CLICK" && (
        <div className="activity-meta mt-2">
          <span>
            <strong>Clicked:</strong> {event.clickedText}
          </span>

          <span>
            <strong>Page:</strong> {event.page}
          </span>
        </div>
      )}

      {event.type === "PAGE_ENTER" && (
        <div className="activity-meta mt-2">
          <span>
            <strong>Page:</strong> {event.page}
          </span>
        </div>
      )}

      {event.type === "PAGE_EXIT" && (
        <div className="activity-meta mt-2">
          <span>
            <strong>Page:</strong> {event.page}
          </span>

          {event.duration && (
            <span>
              <strong>Time spent:</strong> {event.duration}
            </span>
          )}
        </div>
      )}

      {event.type === "API_RESPONSE" && (
        <div className="activity-meta mt-2">
          <span>
            <strong>Status:</strong> {event.status}
          </span>

          {event.method && (
            <span>
              <strong>Method:</strong> {event.method}
            </span>
          )}
        </div>
      )}

      {(event.type === "API_REQUEST" || event.type === "API_RESPONSE") && (
        <div className="mt-2">
          <button
            type="button"
            className="btn btn-sm btn-link p-0 text-decoration-none"
            onClick={() => setShowTechnical((previous) => !previous)}
          >
            {showTechnical
              ? "Hide technical details"
              : "View technical details"}
          </button>

          {showTechnical && (
            <div className="technical-details mt-2">
              <div>
                <strong>API:</strong>{" "}
                <span className="text-break">{event.api || "N/A"}</span>
              </div>

              {event.method && (
                <div>
                  <strong>Method:</strong> {event.method}
                </div>
              )}

              {event.status && (
                <div>
                  <strong>Status:</strong> {event.status}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Activity() {
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [activity, setActivity] = useState([]);

  const [activeTab, setActiveTab] = useState("ALL");

  const [page, setPage] = useState(1);
  const [limit] = useState(50);

  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state?.userData?.email) {
      setEmail(location.state.userData.email);
    }
  }, [location.state]);

  const fetchData = async (currentPage = 1, currentType = activeTab) => {
    if (!email) return;

    try {
      setLoading(true);

      const response = await axios.get(`${API_URL}/api/admin/user_activity`, {
        params: {
          userEmail: email,
          page: currentPage,
          limit,
          type: currentType,
        },
      });

      setActivity(response.data.activity || []);

      setTotalPages(response.data.totalPages || 1);

      setTotalEvents(response.data.totalEvents || 0);

      setPage(response.data.page || currentPage);
    } catch (error) {
      console.error(error);

      if (error.response?.status === 404) {
        setActivity([]);
        setTotalPages(1);
        setTotalEvents(0);

        toast.error("No activity found for this user.");
      } else {
        toast.error("Unable to load user activity.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) {
      fetchData(1, activeTab);
    }
  }, [email, activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handlePrevious = () => {
    if (page > 1) {
      const newPage = page - 1;
      setPage(newPage);
      fetchData(newPage, activeTab);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      const newPage = page + 1;
      setPage(newPage);
      fetchData(newPage, activeTab);
    }
  };

  const latestActivity = activity[0];

  return (
    <div className="body-wrapper py-4">
      <div className="container">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
          <div>
            <h4 className="text-black fw-bold mb-1">User Activity</h4>

            <div className="text-dark">{email}</div>
          </div>

          <div className="activity-summary">
            <div className="summary-label text-black fw-bold fs-4">
              Latest Activity
            </div>

            <div className="summary-value">
              {latestActivity?.timestamp
                ? formatTime(latestActivity.timestamp)
                : "No activity"}
            </div>
          </div>
        </div>

        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="summary-card border">
              <div className="summary-card-label text-black fw-bold fs-5">
                Total Activity
              </div>

              <div className="summary-card-value">{totalEvents}</div>

              <div className="summary-card-description">
                Recorded user activities
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="summary-card border">
              <div className="summary-card-label text-black fw-bold fs-5">
                Latest Activity
              </div>

              <div className="text-dark">
                {latestActivity ? getEventTitle(latestActivity) : "N/A"}
              </div>

              <div className="summary-card-description">
                {latestActivity?.timestamp
                  ? getRelativeDate(latestActivity.timestamp)
                  : "No activity recorded"}
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="summary-card border ">
              <div className="summary-card-label text-black fw-bold fs-5">
                Current View
              </div>

              <div className="summary-card-value">
                {TABS.find((tab) => tab.key === activeTab)?.label ||
                  "All Activity"}
              </div>

              <div className="summary-card-description">
                Showing filtered activity
              </div>
            </div>
          </div>
        </div>

        <div className="activity-tabs mb-4">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`activity-tab ${
                activeTab === tab.key ? "active" : ""
              }`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="card activity-card shadow-sm border-0">
          <div className="card-header bg-white border-0 py-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1 text-black fw-bold">Activity Timeline</h5>

                <small className="text-dark">
                  A simplified view of what the user did in the application
                </small>
              </div>

              <span className="activity-count">{totalEvents} events</span>
            </div>
          </div>

          <div className="card-body p-0">
            {loading ? (
              <div className="activity-empty">
                <div
                  className="spinner-border text-primary mb-3"
                  role="status"
                />

                <div className="fw-semibold text-black">
                  Loading activity...
                </div>

                <small className="text-dark">
                  Please wait while we retrieve the user's activity.
                </small>
              </div>
            ) : activity.length === 0 ? (
              <div className="activity-empty">
                <div className="empty-icon">—</div>

                <h6 className="text-black fw-bold">No activity found</h6>

                <p className="text-dark mb-0">
                  There is no recorded activity for this filter.
                </p>
              </div>
            ) : (
              <div className="activity-timeline">
                {activity.map((event, index) => (
                  <div
                    className="activity-item"
                    key={`${event.timestamp}-${index}`}
                  >
                    <div className="activity-icon">{getEventIcon(event)}</div>

                    <div className="activity-content">
                      <div className="d-flex justify-content-between align-items-start gap-3">
                        <div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className={getBadgeClass(event)}>
                              {getEventTitle(event)}
                            </span>
                          </div>

                          <h6 className="activity-title">{event.message}</h6>
                        </div>

                        <div className="activity-time">
                          <div>{formatShortTime(event.timestamp)}</div>

                          <small>{formatTime(event.timestamp)}</small>
                        </div>
                      </div>

                      <ActivityDetails event={event} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={page <= 1 || loading}
            onClick={handlePrevious}
          >
            ← Previous
          </button>

          <div className="text-center">
            <div className="fw-semibold text-black">
              Page {page} of {totalPages}
            </div>

            <small className="text-dark">{totalEvents} matching events</small>
          </div>

          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={page >= totalPages || loading}
            onClick={handleNext}
          >
            Next →
          </button>
        </div>
      </div>

      <style>
        {`
          .activity-tabs {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            padding: 6px;
            background: #f6f7f9;
            border-radius: 12px;
          }

          .activity-tab {
            border: none;
            background: transparent;
            color: #444;
            padding: 9px 16px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            transition: all 0.2s ease;
          }

          .activity-tab:hover {
            background: #fff;
            color: #6600cc;
          }

          .activity-tab.active {
            background: #6600cc;
            color: #fff;
            box-shadow: 0 2px 8px rgba(102, 0, 204, 0.2);
          }

          .summary-card {
            background: #fff;
            border: 1px solid #eee;
            border-radius: 12px;
            padding: 18px;
            height: 100%;
          }

          .summary-card-label {
            color: #777;
            font-size: 13px;
            margin-bottom: 6px;
          }

          .summary-card-value {
            color: #000;
            font-size: 20px;
            font-weight: 700;
          }

          .summary-card-description {
            color: #999;
            font-size: 12px;
            margin-top: 4px;
          }

          .activity-summary {
            text-align: right;
          }

          .summary-label {
            color: #888;
            font-size: 12px;
          }

          .summary-value {
            color: #111;
            font-weight: 600;
            font-size: 14px;
          }

          .activity-card {
            border-radius: 14px;
            overflow: hidden;
          }

          .activity-count {
            background: #f3efff;
            color: #6600cc;
            border-radius: 20px;
            padding: 6px 12px;
            font-size: 12px;
            font-weight: 700;
          }

          .activity-timeline {
            position: relative;
          }

          .activity-item {
            display: flex;
            gap: 16px;
            padding: 20px 24px;
            border-top: 1px solid #f0f0f0;
            transition: background 0.2s ease;
          }

          .activity-item:hover {
            background: #fafafa;
          }

          .activity-icon {
            min-width: 38px;
            width: 38px;
            height: 38px;
            border-radius: 50%;
            background: #f2eaff;
            color: #6600cc;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 16px;
          }

          .activity-content {
            flex: 1;
            min-width: 0;
          }

          .activity-title {
            color: #111;
            font-weight: 700;
            margin: 9px 0 0;
            font-size: 15px;
          }

          .activity-description {
            color: #555;
            font-size: 14px;
            line-height: 1.5;
          }

          .activity-meta {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
            color: #666;
            font-size: 13px;
          }

          .activity-meta strong {
            color: #333;
          }

          .activity-time {
            text-align: right;
            color: #555;
            white-space: nowrap;
            font-size: 13px;
          }

          .activity-time small {
            color: #999;
            font-size: 11px;
          }

          .technical-details {
            background: #f7f7f8;
            border: 1px solid #e5e5e5;
            border-radius: 8px;
            padding: 10px 12px;
            font-size: 12px;
            color: #555;
          }

          .activity-empty {
            min-height: 260px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            text-align: center;
            padding: 40px;
          }

          .empty-icon {
            width: 42px;
            height: 42px;
            border-radius: 50%;
            background: #f3f3f3;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #777;
            font-size: 20px;
            margin-bottom: 12px;
          }

          @media (max-width: 768px) {
            .activity-item {
              padding: 16px;
              gap: 10px;
            }

            .activity-time {
              font-size: 11px;
            }

            .activity-time small {
              display: none;
            }

            .activity-meta {
              flex-direction: column;
              gap: 4px;
            }

            .activity-summary {
              text-align: left;
            }
          }
        `}
      </style>
    </div>
  );
}
